import { SERIES_NAMES, type SeriesName } from "@/api/recordsSeries";
import {
  WINDOW_VIEW_LINES,
  WINDOW_VIEWS,
  type WindowView,
} from "@/api/windowsSeries";

// The json:chart fence (TTTL-259, TTTL-304): what the chat draws, said by
// the model as a reference — the pipelines and the window the series tool
// was asked about — never as numbers. The block fetches the series itself,
// so a reload redraws the engine's truth and a slip in the model's copying
// cannot bend the line. `kind` is explicit so later kinds (the fields
// series) can join without breaking old fences.
//
// The fence names one pipeline (`pipeline`) or several to compare on one
// plot (`pipelines`), and may pick which lines to draw (`series`). The
// reader normalises both into lists. A lone pipeline draws every line, as
// the first charts did, or the subset the fence names. A comparison draws
// one line per pipeline — watched unless the fence names another — so a
// plot of seven pipelines never carries twenty-one lines.
//
// A pie (`kind: "pie"`) reads the same fields at one moment, the last
// watched in the window: one pipeline splits its records watched into
// records kept, dropped and incomplete, so it takes no `series`; a comparison
// gives each pipeline a slice of one metric. A pie draws one moment, so
// `points` falls away; the block fetches the series at the route's default
// count and reads the last moment watched.
//
// Reading a fence is a chain of small readers — kind, pipelines, series,
// window — each answering with what it read or the one reason it refused,
// so the reasons stay the words the chat shows and no reader has to know
// another's rules. The first two readers settle the chart's `Shape` — its
// kind and its pipelines — and the later readers take that one pair, since
// every remaining rule turns on both and on neither alone.

export const CHART_FENCE_ARTIFACT = "chart";

interface ChartBase {
  /** One to MAX_PIPELINES, each named once. */
  pipelines: string[];
  /** Which counts to draw, in the fence's order; never empty, and one
   *  alone when the chart compares pipelines. */
  series: SeriesName[];
  /** RFC 3339; the route's defaults when absent (the deployment's start,
   *  now). A chart of several pipelines carries `from`: the engine's
   *  default start is each pipeline's own, and one plot needs one window. */
  from?: string;
  to?: string;
  title?: string;
}

/** Records over time: a line per count, or per pipeline. */
export interface RecordsChart extends ChartBase {
  kind: "records";
  /** Moments along the axis; the route's default, 30, when absent. */
  points?: number;
}

/** Records at the last moment watched: one pipeline's three counts as
 *  slices of its records watched, or a slice per pipeline of one count. */
export interface PieChart extends ChartBase {
  kind: "pie";
}

/** A windowed pipeline's store over time (`kind: "windows"`): one
 *  pipeline, one view of the store, its lines or a subset of them. Each
 *  moment is the newest sample of the shared store, never a sum, so
 *  pipelines are not compared. A pie (`shape: "pie"`) reads the partition
 *  states at the last moment that read them. */
export interface WindowsChart extends Omit<ChartBase, "series"> {
  kind: "windows";
  view: WindowView;
  /** The view's lines to draw; all of them unless the fence names some. */
  series: string[];
  points?: number;
  shape?: "pie";
}

/** The records charts: what the records series draws. */
export type SeriesChart = RecordsChart | PieChart;

export type ChartFence = SeriesChart | WindowsChart;
export type ChartKind = ChartFence["kind"];

const MAX_POINTS = 500;
/** As many pipelines as the overlay palette has hues. */
export const MAX_PIPELINES = 7;

/** What a reader answers: what it read, or the one reason it refused. */
type Read<T> = T | { error: string };

type Edges = Pick<ChartBase, "from" | "to">;
type Window = Pick<RecordsChart, "from" | "to" | "points">;

/** The chart the fence asked for, before its lines and its window are
 *  read: the kind and the pipelines travel together because every rule
 *  left — which lines draw by default, how many a comparison may draw,
 *  whether the window has to say `from` — reads both. */
interface Shape {
  kind: SeriesChart["kind"];
  pipelines: string[];
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

const MOMENT =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;

/** A matched moment's fields, named, so the checks below read them by
 *  name instead of counting capture groups. */
interface Moment {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
  zone: string;
}

/** One field of a moment and the range it has to fall in. */
interface Limit {
  field: string;
  min: number;
  max: number;
}

/** RFC 3339 as the engine reads it: a date, a T, a time, a zone, and each
 *  part within its range, so "2026-99-99T99:99:99Z" is refused here
 *  rather than sent to the engine. */
function isMoment(v: unknown): v is string {
  if (typeof v !== "string") return false;
  const parts = MOMENT.exec(v);
  return parts !== null && partsInRange(parts);
}

/** Every field of a matched moment within its own range, the day's
 *  within its month's length, and the zone a real offset. */
function partsInRange(parts: RegExpExecArray): boolean {
  const moment = asMoment(parts);
  const limits: Limit[] = [
    { field: moment.month, min: 1, max: 12 },
    { field: moment.day, min: 1, max: daysInMonth(moment) },
    { field: moment.hour, min: 0, max: 23 },
    { field: moment.minute, min: 0, max: 59 },
    { field: moment.second, min: 0, max: 60 },
  ];
  return limits.every(inRange) && isZone(moment);
}

/** The match's groups under their names; a fence may leave the seconds
 *  out, and a moment without them is on the minute. */
function asMoment(parts: RegExpExecArray): Moment {
  const [, year, month, day, hour, minute, second = "0", zone] = parts;
  return { year, month, day, hour, minute, second, zone };
}

function inRange(limit: Limit): boolean {
  const n = Number(limit.field);
  return n >= limit.min && n <= limit.max;
}

function daysInMonth(moment: Moment): number {
  const { year, month } = moment;
  return new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate();
}

function isZone(moment: Moment): boolean {
  const { zone } = moment;
  if (zone === "Z") return true;
  return (
    inRange({ field: zone.slice(1, 3), min: 0, max: 23 }) &&
    inRange({ field: zone.slice(4, 6), min: 0, max: 59 })
  );
}

/** How long a fetched series stays fresh: forever for a closed window,
 *  which cannot change, and half a minute for one open to now, so a
 *  later chart of the same pipeline asks the engine again instead of
 *  showing the first chart's traffic as today's. */
export function seriesFreshnessMs(chart: ChartFence): number {
  return chart.to ? Number.POSITIVE_INFINITY : 30_000;
}

/** The fence's JSON as an object, or nothing: unreadable text and a bare
 *  list are the same refusal to the reader. */
function readJson(code: string): Record<string, unknown> | undefined {
  try {
    const parsed: unknown = JSON.parse(code);
    return isRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/** The kind, explicit so an older block refuses a later kind by name
 *  rather than drawing it wrong. */
function readKind(v: unknown): Read<{ kind: SeriesChart["kind"] }> {
  if (v !== "records" && v !== "pie") {
    return { error: `unknown chart kind ${JSON.stringify(v)}` };
  }
  return { kind: v };
}

function readEdges(r: Record<string, unknown>): Read<{ edges: Edges }> {
  const edges: Edges = {};
  for (const edge of ["from", "to"] as const) {
    if (r[edge] === undefined) continue;
    if (!isMoment(r[edge]))
      return { error: `${edge} is not an RFC 3339 moment` };
    edges[edge] = r[edge];
  }
  return { edges };
}

function isPointCount(v: unknown): v is number {
  if (typeof v !== "number") return false;
  if (!Number.isInteger(v)) return false;
  return v >= 1 && v <= MAX_POINTS;
}

function readWindow(r: Record<string, unknown>): Read<{ window: Window }> {
  const read = readEdges(r);
  if ("error" in read) return read;
  if (r.points === undefined) return { window: read.edges };
  if (!isPointCount(r.points)) {
    return { error: `points must be between 1 and ${MAX_POINTS}` };
  }
  return { window: { ...read.edges, points: r.points } };
}

/** The window, plus the rule that joins it to the pipelines: one plot
 *  needs one start, and the engine's default start is each pipeline's
 *  own, so a comparison has to say `from`. */
function readChartWindow(
  r: Record<string, unknown>,
  shape: Shape,
): Read<{ window: Window }> {
  const read = readWindow(r);
  if ("error" in read) return read;
  if (compares(shape) && read.window.from === undefined) {
    return { error: "a chart of several pipelines needs from" };
  }
  return read;
}

/** Whether the chart puts pipelines side by side, which is where the
 *  rules on default lines, line counts and `from` part ways. */
function compares(shape: Shape): boolean {
  return shape.pipelines.length > 1;
}

function isName(v: unknown): v is string {
  return typeof v === "string" && v.trim() !== "";
}

/** A list of names and nothing else: a fence that names no pipeline at
 *  all, an empty list and a blank name all fail here together. */
function areNames(v: unknown): v is string[] {
  if (!Array.isArray(v)) return false;
  return v.length > 0 && v.every(isName);
}

/** The pipelines the fence names: `pipelines` when present, else the lone
 *  `pipeline`. Each named once, at most the palette's worth. */
function readPipelines(
  r: Record<string, unknown>,
): Read<{ pipelines: string[] }> {
  const named = r.pipelines !== undefined ? r.pipelines : [r.pipeline];
  if (!areNames(named)) {
    return { error: "the chart names no pipeline" };
  }
  const twice = firstRepeat(named);
  if (twice !== undefined) {
    return { error: `the chart names ${twice} twice` };
  }
  if (named.length > MAX_PIPELINES) {
    return { error: `a chart holds at most ${MAX_PIPELINES} pipelines` };
  }
  return { pipelines: named };
}

function isSeriesName(v: unknown): v is SeriesName {
  return (SERIES_NAMES as readonly unknown[]).includes(v);
}

/** What a fence that picks no lines draws: every count of a lone
 *  pipeline, and watched alone when pipelines are compared. */
function defaultSeries(shape: Shape): SeriesName[] {
  return compares(shape) ? ["watched"] : [...SERIES_NAMES];
}

/** The fence's own list of counts, read as names: known, and each once. */
function readNamedSeries(v: unknown): Read<{ series: SeriesName[] }> {
  if (!Array.isArray(v) || v.length === 0) {
    return { error: "series names no line" };
  }
  const unknown = v.find((name) => !isSeriesName(name));
  if (unknown !== undefined) {
    return { error: `unknown series ${JSON.stringify(unknown)}` };
  }
  const twice = firstRepeat(v);
  if (twice !== undefined) return { error: `series names ${twice} twice` };
  return { series: v };
}

/** A pie of one pipeline draws the split of its records watched, which is
 *  the three counts and nothing else, so naming lines is a mistake worth
 *  saying rather than a list to read. */
function fixedSeriesError(shape: Shape): string | undefined {
  if (shape.kind === "pie" && !compares(shape)) {
    return "a pie of one pipeline splits records watched";
  }
  return undefined;
}

/** A comparison gives each pipeline one line, or one slice, so seven
 *  pipelines never carry twenty-one of either. */
function tooManyLinesError(
  shape: Shape,
  drawn: SeriesName[],
): string | undefined {
  if (!compares(shape) || drawn.length < 2) return undefined;
  return shape.kind === "pie"
    ? "a pie of several pipelines draws one slice each"
    : "a chart of several pipelines draws one line each";
}

/** The counts to draw: the fence's own list, or the default for the
 *  chart's shape. */
function readSeries(
  r: Record<string, unknown>,
  shape: Shape,
): Read<{ series: SeriesName[] }> {
  if (r.series === undefined) return { series: defaultSeries(shape) };
  const fixed = fixedSeriesError(shape);
  if (fixed !== undefined) return { error: fixed };
  const read = readNamedSeries(r.series);
  if ("error" in read) return read;
  const tooMany = tooManyLinesError(shape, read.series);
  return tooMany === undefined ? read : { error: tooMany };
}

function firstRepeat(names: string[]): string | undefined {
  const seen = new Set<string>();
  for (const name of names) {
    if (seen.has(name)) return name;
    seen.add(name);
  }
  return undefined;
}

/** What was read, as the kind's own chart: a pie reads one moment, so it
 *  leaves `points` behind. */
function assembleChart(
  shape: Shape,
  series: SeriesName[],
  window: Window,
): SeriesChart {
  const { kind, pipelines } = shape;
  const { points, ...edges } = window;
  const base = { pipelines, series, ...edges };
  if (kind === "pie") return { kind, ...base };
  return { kind, ...base, ...(points === undefined ? {} : { points }) };
}

/** The fence's title when it says one worth showing. */
function withTitle(chart: ChartFence, title: unknown): ChartFence {
  if (!isName(title)) return chart;
  return { ...chart, title };
}

/** The fence read into a chart, or the reason it cannot be drawn — shown
 *  beside the raw block, never thrown. */
export function parseChartFence(code: string): Read<{ chart: ChartFence }> {
  const parsed = readJson(code);
  if (parsed === undefined) return { error: "couldn't read this chart" };
  if (parsed.kind === "windows") return parseWindowsFence(parsed);
  const kind = readKind(parsed.kind);
  if ("error" in kind) return kind;
  const named = readPipelines(parsed);
  if ("error" in named) return named;
  const shape: Shape = { ...kind, ...named };
  const counts = readSeries(parsed, shape);
  if ("error" in counts) return counts;
  const read = readChartWindow(parsed, shape);
  if ("error" in read) return read;
  const chart = assembleChart(shape, counts.series, read.window);
  return { chart: withTitle(chart, parsed.title) };
}

function isWindowView(v: unknown): v is WindowView {
  return (WINDOW_VIEWS as readonly unknown[]).includes(v);
}

/** The view of the store: required, since the views share no lines. */
function readView(v: unknown): Read<{ view: WindowView }> {
  if (v === undefined) return { error: "a windows chart needs a view" };
  if (!isWindowView(v)) return { error: `unknown view ${JSON.stringify(v)}` };
  return { view: v };
}

/** The view's lines to draw: all of them, or the fence's own list of
 *  them, each known and named once. */
function readViewSeries(
  v: unknown,
  view: WindowView,
): Read<{ series: string[] }> {
  const lines = WINDOW_VIEW_LINES[view];
  if (v === undefined) return { series: [...lines] };
  if (!Array.isArray(v) || v.length === 0) {
    return { error: "series names no line" };
  }
  const unknown = v.find((name) => !lines.includes(name));
  if (unknown !== undefined) {
    return { error: `unknown series ${JSON.stringify(unknown)}` };
  }
  const twice = firstRepeat(v);
  if (twice !== undefined) return { error: `series names ${twice} twice` };
  return { series: v };
}

/** A pie of the store reads the partition states at one moment, so it is
 *  the one view a pie can draw, and it takes no points. */
function readWindowsShape(
  r: Record<string, unknown>,
  view: WindowView,
): Read<{ shape?: "pie" }> {
  if (r.shape === undefined) return {};
  if (r.shape !== "pie") {
    return { error: `unknown shape ${JSON.stringify(r.shape)}` };
  }
  if (view !== "partition_states") {
    return { error: "a pie of windows draws partition_states" };
  }
  if (r.points !== undefined) {
    return { error: "a pie of windows reads one moment and takes no points" };
  }
  return { shape: "pie" };
}

/** A windows fence read into its chart, or the one reason it cannot be
 *  drawn. */
function parseWindowsFence(
  r: Record<string, unknown>,
): Read<{ chart: ChartFence }> {
  const named = readPipelines(r);
  if ("error" in named) return named;
  if (named.pipelines.length !== 1) {
    return { error: "a windows chart draws one pipeline" };
  }
  const view = readView(r.view);
  if ("error" in view) return view;
  const lines = readViewSeries(r.series, view.view);
  if ("error" in lines) return lines;
  const shape = readWindowsShape(r, view.view);
  if ("error" in shape) return shape;
  const window = readWindow(r);
  if ("error" in window) return window;
  const chart: WindowsChart = {
    kind: "windows",
    pipelines: named.pipelines,
    view: view.view,
    series: lines.series,
    ...window.window,
    ...shape,
  };
  return { chart: withTitle(chart, r.title) };
}
